"use client";

/**
 * sak's settings content, shared by the in-app settings dialog (the default
 * path) and the separate settings window (?view=settings — settings-window
 * plan). MINIMAL but real: an about-style General group and an Appearance
 * group wired to the suite's shared theme runtime, plus the suite-wide
 * "open settings in a separate window" preference. All rows use the shared
 * @suite/settings primitives over the served suite-ui.css classes.
 */

import { SettingGroup, SettingRow, SettingSelect, SettingsWindowPreferenceToggle } from "@suite/settings";
import { useTourReplayEntry } from "@suite/tour";
import { useTranslation } from "react-i18next";

import { useSakTour, useSakTourLabels } from "@/tour/sakTour";
import { useTheme, type ThemeMode } from "@/hooks/useTheme";

/** No-op close for surfaces that own none (the settings window closes itself). */
const noop = (): void => {};

export function SettingsGeneralSection({ onClosePanel }: { onClosePanel?: () => void }) {
  const { t } = useTranslation();

  // Replay row: closes this surface and restarts the first-run tour; in the
  // separate settings window the hook relays the request to the main window
  // and closes this window instead.
  const replayTour = useTourReplayEntry(useSakTour(), useSakTourLabels(), onClosePanel ?? noop);

  return (
    <SettingGroup title={t("settings.general")}>
      <SettingRow label="Sak" hint={t("settings.about")} />
      <SettingRow
        label={t("settings.separateWindow")}
        hint={t("settings.separateWindowDesc")}
        control={<SettingsWindowPreferenceToggle />}
      />
      <SettingRow
        label={t("tour.replay")}
        control={
          <button
            type="button"
            className="whitespace-nowrap rounded-md border border-border-color px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted"
            onClick={replayTour}
          >
            {t("tour.start")}
          </button>
        }
      />
    </SettingGroup>
  );
}

export function SettingsAppearanceSection() {
  const { t } = useTranslation();
  const { mode, setMode } = useTheme();

  const options = [
    { value: "system" as const, label: t("common.theme.system") },
    { value: "light" as const, label: t("common.theme.light") },
    { value: "dark" as const, label: t("common.theme.dark") },
  ];

  return (
    <SettingGroup title={t("settings.appearance")}>
      <SettingRow
        label={t("common.theme.label")}
        control={
          <SettingSelect
            value={mode}
            options={options}
            onChange={(next) => setMode(next as ThemeMode)}
            label={t("common.theme.label")}
          />
        }
      />
    </SettingGroup>
  );
}
